import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '../../hooks/useLocalizedField';
import {
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Box,
  FormLabel,
  FormHelperText,
  Alert,
  Link
} from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { CurrentEvent, DEFAULT_REGISTRATION_CONFIG, RegistrationConfig } from '../../contexts/EventEditionContext';

// License number format: NNNNNN-YYYY (e.g., 221393-2025)
const LICENSE_NUMBER_REGEX = /^\d{6}-\d{4}$/;

interface RaceDetailsFormProps {
  formData: {
    raceDistance: string;
    travelRequired: string;
    comments: string;
    hasYearLicense?: boolean;
    licenseNumber?: string;
    dateOfBirth?: Date | string | null;
    yearOfBirth?: number | null;
    [key: string]: any;
  };
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
  fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>>;
  onBlur?: (field: string) => void;
  event: CurrentEvent;
  touchedFields?: Record<string, boolean>;
}

const RaceDetailsForm: React.FC<RaceDetailsFormProps> = ({ 
  formData, 
  onChange, 
  errors, 
  fieldRefs, 
  onBlur, 
  event,
  touchedFields = {}
}) => {
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const firstRadioRef = useRef<HTMLInputElement>(null);

  const eventYear = useMemo(() => {
    const d = event?.startTime instanceof Date ? event.startTime : new Date(event?.startTime as any);
    const year = d && !Number.isNaN(d.getTime()) ? d.getFullYear() : new Date().getFullYear();
    return year;
  }, [event?.startTime]);

  const licenseExample = useMemo(() => `123456-${eventYear}`, [eventYear]);

  // Get registration config with defaults
  const config: RegistrationConfig = {
    fields: {
      ...DEFAULT_REGISTRATION_CONFIG.fields,
      ...event.registrationConfig?.fields
    }
  };

  // Get the selected race distance to check if license is required
  const selectedDistance = event.raceDistances?.find(d => d.id === formData.raceDistance);
  const licenseFee = (event.fees as any)?.oneDayLicense ?? event.fees?.oneTimeLicense ?? 0;
  
  const distanceNameEn = (selectedDistance?.displayName ?? '').toLowerCase();
  const distanceNameNo = (selectedDistance?.displayName_no ?? '').toLowerCase();
  const distanceId = (selectedDistance?.id ?? '').toLowerCase();
  
  // Hiking class detection - explicitly exclude from license requirement
  const isHikingClass =
    distanceNameEn.includes('hiking') ||
    distanceNameEn.includes('hike') ||
    distanceNameNo.includes('turklasse') ||
    distanceNameNo.includes('tur') ||
    distanceId.includes('hiking') ||
    distanceId.includes('hike') ||
    distanceId.includes('tur');
  
  // Competition class detection
  const isCompetitionClass =
    distanceNameEn.includes('competition') ||
    distanceNameNo.includes('konkurranse') ||
    distanceId.includes('competition') ||
    distanceId.includes('konkurranse') ||
    distanceId === 'comp';
  
  // Timed recreation class detection (requires license like competition)
  // Matches: "Timed recreation", "Trim med tidtaking", "trim_tidtaking", etc.
  const isTimedRecreationClass =
    distanceNameEn.includes('timed') ||
    distanceNameNo.includes('tidtaking') ||
    distanceId.includes('timed') ||
    distanceId.includes('tidtaking') ||
    distanceId === 'trim' ||
    distanceId === 'trim_tidtaking' ||
    distanceId === 'timedrecreation' ||
    distanceId === 'timed_recreation';
  
  // License required for Competition and Timed Recreation, but NOT Hiking
  const requiresLicense =
    !isHikingClass &&
    (licenseFee > 0 || isCompetitionClass || isTimedRecreationClass);

  // Calculate age-based license exemption for Malvikingen Opp
  const ageExemption = useMemo(() => {
    // Only apply to Malvikingen Opp events
    if (!event.id.includes('mo') && !event.eventShortName?.toLowerCase().includes('malvikingen')) {
      return null;
    }

    // Must have either dateOfBirth or yearOfBirth, and event end time
    if (!event.endTime) {
      return null;
    }

    // If both dateOfBirth and yearOfBirth are missing, assume license is required
    if (!formData.dateOfBirth && !(formData as any).yearOfBirth) {
      return null;
    }

    try {
      const eventEndDate = new Date(event.endTime);
      
      // Check if event date is valid
      if (isNaN(eventEndDate.getTime())) {
        return null;
      }

      const eventYear = eventEndDate.getFullYear();
      let actualAge: number | null = null;

      // Try to calculate exact age using dateOfBirth first
      if (formData.dateOfBirth) {
        const birthDate = typeof formData.dateOfBirth === 'object' && formData.dateOfBirth instanceof Date 
          ? formData.dateOfBirth 
          : new Date(formData.dateOfBirth);
        
        // Check if birth date is valid
        if (!isNaN(birthDate.getTime())) {
          // Calculate exact age on event day
          const ageOnEventDay = eventYear - birthDate.getFullYear();
          const monthDiff = eventEndDate.getMonth() - birthDate.getMonth();
          
          // Adjust age if birthday hasn't occurred yet this year
          actualAge = monthDiff < 0 || (monthDiff === 0 && eventEndDate.getDate() < birthDate.getDate()) 
            ? ageOnEventDay - 1 
            : ageOnEventDay;
        }
      }

      // If we couldn't calculate exact age, try using yearOfBirth as fallback
      if (actualAge === null && (formData as any).yearOfBirth) {
        actualAge = eventYear - (formData as any).yearOfBirth;
      }

      // If we still couldn't determine age, assume license is required
      if (actualAge === null) {
        return null;
      }

      // Check for under 13 exemption
      if (actualAge < 13) {
        return {
          type: 'under_13' as const,
          age: actualAge,
          message: t('form.under13Exemption'),
          linkUrl: 'https://www.friidrett.no/link/ad805ac66fbb4f4bad4b20054da97e03.aspx',
          linkText: t('form.readMore')
        };
      }

      // Check for 81+ exemption (if they turn 81 the same year as the event)
      // For yearOfBirth fallback, we already used eventYear - yearOfBirth = actualAge
      // So if actualAge >= 81, the condition is already met
      if (actualAge >= 81) {
        return {
          type: 'over_80' as const,
          age: actualAge,
          message: t('form.over80Exemption'),
          linkUrl: null,
          linkText: null
        };
      }

      return null; // No exemption, license required
    } catch (error) {
      console.warn('Error calculating age exemption:', error);
      return null;
    }
  }, [event.id, event.eventShortName, event.endTime, formData, t]);

  // Final determination: license required only if requiresLicense AND no age exemption
  const finalRequiresLicense = requiresLicense && !ageExemption;

  // Effect to focus on the first radio button when the form mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    if (firstRadioRef.current) {
      firstRadioRef.current.focus();
    }
  }, [event]);


  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        {t('form.raceDetails')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('form.raceDetailsDesc')}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl 
            component="fieldset" 
            required 
            error={!!(errors.raceDistance && touchedFields.raceDistance)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <FormLabel component="legend">{t('form.selectDistance')}</FormLabel>
            <RadioGroup
              aria-label="race-distance"
              name="raceDistance"
              value={formData.raceDistance}
              onChange={(e) => {
                onChange('raceDistance', e.target.value);
              }}
              onBlur={() => onBlur && onBlur('raceDistance')}
              ref={fieldRefs.raceDistance as any}
              sx={{ gap: 1 }}
            >
              {(event.raceDistances || []).map((distance, idx) => (
                <FormControlLabel
                  key={distance.id}
                  value={distance.id}
                  control={
                    <Radio inputRef={idx === 0 ? firstRadioRef : undefined} />
                  }
                  label={getLocalizedField(distance, 'displayName') || distance.id}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                />
              ))}
            </RadioGroup>
            <FormHelperText error={!!(errors.raceDistance && touchedFields.raceDistance)}>
              {errors.raceDistance && touchedFields.raceDistance ? errors.raceDistance : t('form.raceDetailsDesc')}
            </FormHelperText>
          </FormControl>
        </Grid>
        
        {/* License Section - only show if a race is selected and license is required */}
        {formData.raceDistance && requiresLicense && (
          <Grid item xs={12}>
            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              {ageExemption ? (
                // Show exemption message instead of license fields
                <Box>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600} color="success.main">
                    ✅ {t('form.licenseExemptionTitle')}
                  </Typography>
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {ageExemption.message}
                      {ageExemption.linkUrl && (
                        <>
                          {' '}
                          <Link
                            href={ageExemption.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            {ageExemption.linkText}
                            <ExternalLink size={14} />
                          </Link>
                        </>
                      )}
                    </Typography>
                  </Alert>
                </Box>
              ) : (
                // Show standard license fields
                <Box>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    {t('form.licenseSection')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('form.hasYearLicenseHelper')}
                  </Typography>
                  
                  <FormControl 
                    component="fieldset" 
                    fullWidth
                    error={!!errors.hasYearLicense}
                    required
                  >
                    <FormLabel component="legend">{t('form.hasYearLicense')}</FormLabel>
                    <RadioGroup
                      aria-label="has-year-license"
                      name="hasYearLicense"
                      value={formData.hasYearLicense === true ? 'yes' : formData.hasYearLicense === false ? 'no' : ''}
                      onChange={(e) => {
                        const hasLicense = e.target.value === 'yes';
                        onChange('hasYearLicense', hasLicense);
                        // Clear license number if switching to "no"
                        if (!hasLicense) {
                          onChange('licenseNumber', '');
                        }
                      }}
                      onBlur={() => onBlur && onBlur('hasYearLicense')}
                      ref={fieldRefs.hasYearLicense as any}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label={t('form.licenseYes')}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label={t('form.licenseNo')}
                      />
                    </RadioGroup>
                    {errors.hasYearLicense && (
                      <FormHelperText error>{errors.hasYearLicense}</FormHelperText>
                    )}
                  </FormControl>

                  {/* Show license number field if user has year license */}
                  {formData.hasYearLicense === true && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        required
                        id="licenseNumber"
                        name="licenseNumber"
                        label={t('form.licenseNumber')}
                        fullWidth
                        variant="outlined"
                        value={formData.licenseNumber || ''}
                        onChange={(e) => onChange('licenseNumber', e.target.value)}
                        onBlur={() => onBlur && onBlur('licenseNumber')}
                        error={!!errors.licenseNumber}
                        helperText={errors.licenseNumber || t('form.licenseNumberHelper', { example: licenseExample })}
                        inputRef={fieldRefs.licenseNumber as any}
                        inputProps={{ pattern: LICENSE_NUMBER_REGEX.source }}
                        placeholder={licenseExample}
                      />
                      <Typography variant="body2" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link
                          href="https://isonen.no/event/cm1qawqik00o613hlk5o1kjjq/#eventContentTableId"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {t('form.lookupLicense')}
                          <ExternalLink size={14} />
                        </Link>
                      </Typography>
                    </Box>
                  )}

                  {/* Show info about license fee */}
                  {formData.hasYearLicense === true && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {t('form.yearLicenseInfo')}
                    </Alert>
                  )}
                  {formData.hasYearLicense === false && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t('form.oneTimeLicenseInfo', { amount: licenseFee })}
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        )}

        {/* Travel Required - only show if configured */}
        {config.fields.travelRequired && (
          <Grid item xs={12}>
            <TextField
              id="travelRequired"
              name="travelRequired"
              label={t('form.travelRequired') || 'Travel Required'}
              required
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.travelRequired}
              onChange={(e) => onChange('travelRequired', e.target.value)}
              onBlur={() => onBlur && onBlur('travelRequired')}
              inputProps={{ maxLength: 200 }}
              error={!!(errors.travelRequired && touchedFields.travelRequired)}
              helperText={
                errors.travelRequired && touchedFields.travelRequired 
                  ? errors.travelRequired 
                  : t('form.travelHelper') || 'Please describe your travel plans'
              }
              inputRef={fieldRefs.travelRequired as any}
            />
          </Grid>
        )}
        
        {/* Comments - only show if configured */}
        {config.fields.comments && (
          <Grid item xs={12}>
            <TextField
              id="comments"
              name="comments"
              label={t('form.comments')}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={formData.comments}
              onChange={(e) => onChange('comments', e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={t('form.commentsHelper')}
            />
          </Grid>
        )}
        {/* Terms and conditions moved to the Review & Submit page */}
      </Grid>
    </Box>
  );
};

export default RaceDetailsForm;
